<template>
  <main class="verification-page">
    <div class="verification-shell">
      <header class="verification-header">
        <img class="ism-brand" src="../assets/ism-education-logo.png" alt="ISM Education" width="702" height="300" />
        <span class="header-label">Certificate verification</span>
      </header>

      <section class="verification-content" aria-labelledby="verification-heading" :aria-busy="loading ? 'true' : 'false'">
        <div v-if="loading" class="state-card" role="status" aria-live="polite">
          <v-progress-circular indeterminate color="#173c52" size="36" width="3" />
          <p class="eyebrow">ISM Education · Certificate record</p>
          <h1 id="verification-heading">Checking your certificate</h1>
          <p>We’re checking this reference against ISM Education’s issued certificate records.</p>
        </div>

        <template v-else-if="details">
          <div class="page-introduction">
            <p class="eyebrow">Qualification verification</p>
            <h1 id="verification-heading">Certificate verified</h1>
            <p>This certificate matches an issued record held by ISM Education.</p>
          </div>

          <article class="certificate-record" aria-label="Verified certificate details">
            <div class="record-heading">
              <span class="record-label">ISM Education <span aria-hidden="true">/</span> Certificate record</span>
              <span class="status-pill">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
                Verified
              </span>
            </div>

            <div class="record-body">
              <div class="record-details">
                <dl class="award-details">
                  <dt class="eyebrow">Awarded to</dt>
                  <dd class="recipient-name">{{ details.fullName }}</dd>
                  <dt class="eyebrow qualification-label">Qualification</dt>
                  <dd class="qualification-name">{{ details.qualificationName }}</dd>
                </dl>
                <dl class="record-metadata">
                  <div v-for="field in fields" :key="field.key" class="metadata-item">
                    <dt>{{ field.label }}</dt>
                    <dd>{{ details[field.key] }}</dd>
                  </div>
                </dl>
              </div>

              <aside class="verification-assurance" aria-label="Verification confirmation">
                <div class="verification-seal" aria-hidden="true">
                  <svg viewBox="0 0 64 64"><path class="seal-shield" d="M32 9c7 5 14 7 20 8v16c0 11-9 19-20 23C21 52 12 44 12 33V17c6-1 13-3 20-8Z" /><path class="seal-check" d="m23 32 6 6 13-14" /></svg>
                </div>
                <p class="assurance-title">Verified by <span class="assurance-issuer">ISM Education</span></p>
                <span class="assurance-rule" aria-hidden="true"></span>
                <p class="assurance-copy">These details match an E-Certificate issued by ISM Education.</p>
              </aside>
            </div>

            <div class="record-footer">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10v1" /></svg>
              <p>Compare these details with the certificate presented to you.</p>
            </div>
          </article>

          <section class="verification-explanation" aria-labelledby="about-verification">
            <h2 id="about-verification">About this verification</h2>
            <p>This page confirms that the certificate reference matches an issued E-Certificate and the associated certification is currently approved. Only the basic award details are displayed to protect the recipient’s privacy.</p>
          </section>
        </template>

        <div v-else class="state-card">
          <div class="state-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v6m0 3v1" /></svg>
          </div>
          <p class="eyebrow">Certificate verification</p>
          <template v-if="error">
            <h1 id="verification-heading">Verification temporarily unavailable</h1>
            <p>We’re unable to check the certificate record at the moment. Please try again in a little while.</p>
            <v-btn class="retry-button" color="#173c52" dark depressed @click="verify">Try again</v-btn>
          </template>
          <template v-else>
            <h1 id="verification-heading">Certificate could not be verified</h1>
            <p>We could not confirm an approved certificate for this reference. Please check the link or contact ISM Education for assistance.</p>
          </template>
        </div>
      </section>

      <footer class="site-footer">
        <span>ISM Education</span>
        <span>Public certificate verification <span aria-hidden="true">·</span> No sign-in required</span>
      </footer>
    </div>
  </main>
</template>

<script>
export default {
  data() {
    return {
      loading: true, error: false, details: null,
      fields: [
        { key: "qualificationCode", label: "Qualification code" },
        { key: "certificateNumber", label: "Certificate number" },
        { key: "awardDate", label: "Award date" },
      ],
    };
  },
  created() {
    this.previousTitle = document.title;
    document.title = "Certificate verification | ISM Education";
    this.robots = document.createElement("meta");
    this.robots.name = "robots";
    this.robots.content = "noindex, nofollow";
    document.head.appendChild(this.robots);
    this.verify();
  },
  beforeDestroy() {
    this.robots.remove();
    document.title = this.previousTitle;
  },
  watch: { "$route.params.token": "verify" },
  methods: {
    async verify() {
      const token = this.$route.params.token;
      this.loading = true;
      this.error = false;
      this.details = null;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/certificate-verifications/${encodeURIComponent(token)}`,
          { cache: "no-store", credentials: "omit" }
        );
        if (token !== this.$route.params.token) return;
        if (response.status === 404) return;
        if (!response.ok) throw new Error("Verification unavailable");
        const data = await response.json();
        if (token === this.$route.params.token && data.status === "verified") this.details = data;
      } catch (err) {
        if (token === this.$route.params.token) this.error = true;
      } finally {
        if (token === this.$route.params.token) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.verification-page {
  min-height: 100vh;
  background: #f2f5f5;
  color: #173c52;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  padding: 0 32px;
}
.verification-shell { max-width: 1060px; margin: 0 auto; }
.verification-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 24px; padding: 28px 0; border-bottom: 1px solid #dce3e5;
}
.ism-brand { display: block; width: 166px; height: auto; }
.header-label { font-size: 12px; letter-spacing: 1.4px; text-transform: uppercase; color: #536b79; }
.verification-content { padding: 48px 0 36px; }
.page-introduction { margin-bottom: 30px; }
.verification-page .eyebrow { margin: 0 0 12px; font-size: 11px; font-weight: 600; letter-spacing: 1.8px; text-transform: uppercase; color: #5e717c; }
.verification-page h1 { margin: 0 0 14px; font-family: Georgia, "Times New Roman", serif; font-size: 42px; font-weight: 400; line-height: 1.15; letter-spacing: -0.8px; color: #173c52; text-wrap: balance; }
.page-introduction > p:last-child { margin: 0; color: #536b79; font-size: 16px; line-height: 1.65; }
.certificate-record { position: relative; background: white; border: 1px solid #dce2e4; border-top: 4px solid #173c52; border-radius: 3px; box-shadow: 0 12px 40px #173c5208; }
.record-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 32px; border-bottom: 1px solid #e8edef; }
.record-label { font-size: 12px; color: #536b79; }
.record-label > span { margin: 0 10px; color: #a2b1b9; }
.status-pill { display: inline-flex; align-items: center; gap: 7px; padding: 6px 11px; border-radius: 30px; background: #edf6f0; color: #27644c; font-size: 12px; font-weight: 600; }
.status-pill svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.record-body { display: grid; grid-template-columns: minmax(0, 1fr) 250px; }
.record-details { padding: 38px 32px 40px; }
.award-details, .record-metadata { margin: 0; }
.award-details dd { margin-left: 0; overflow-wrap: anywhere; }
.verification-page .recipient-name { font-family: Georgia, "Times New Roman", serif; font-size: 34px; line-height: 1.25; color: #173c52; }
.verification-page .qualification-label { margin-top: 30px; }
.verification-page .qualification-name { font-size: 20px; font-weight: 500; line-height: 1.5; color: #2f4e60; }
.record-metadata { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; margin-top: 32px; padding-top: 24px; border-top: 1px solid #e5ebed; }
.metadata-item dt { color: #5d727f; font-size: 11px; line-height: 1.5; }
.metadata-item dd { margin: 7px 0 0; color: #173c52; font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.verification-assurance { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px 26px; background: #faf9f5; border-left: 1px solid #e8e4d9; text-align: center; }
.verification-seal { display: flex; align-items: center; justify-content: center; width: 98px; height: 98px; padding: 19px; border: 1px solid #b9a780; border-radius: 50%; outline: 1px solid #d9cfb9; outline-offset: 5px; color: #8e784e; }
.verification-seal svg { width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
.seal-check { stroke: #27644c; stroke-width: 3; }
.verification-page .assurance-title { margin: 25px 0 16px; color: #173c52; font-family: Georgia, "Times New Roman", serif; font-size: 22px; line-height: 1.35; }
.assurance-issuer { display: block; }
.assurance-rule { width: 30px; height: 1px; background: #b9a780; margin-bottom: 16px; }
.verification-page .assurance-copy { margin: 0; max-width: 190px; color: #63706f; font-size: 12px; line-height: 1.8; }
.record-footer { display: flex; align-items: center; gap: 10px; padding: 16px 32px; border-top: 1px solid #e8edef; background: #fcfdfd; color: #607580; }
.record-footer svg { width: 17px; height: 17px; flex-shrink: 0; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; }
.verification-page .record-footer p { margin: 0; font-size: 12px; line-height: 1.6; }
.verification-explanation { max-width: 750px; padding: 28px 0 0; }
.verification-page .verification-explanation h2 { margin: 0 0 8px; font-size: 14px; font-weight: 600; color: #2f4e60; }
.verification-page .verification-explanation p { margin: 0; font-size: 13px; line-height: 1.85; color: #5b707d; }
.site-footer { display: flex; justify-content: space-between; gap: 16px; padding: 22px 0 28px; border-top: 1px solid #dce3e5; color: #5b707d; font-size: 11px; line-height: 1.7; }
.site-footer > span:first-child { color: #3e5b6c; font-weight: 600; }
.state-card { padding: 56px 32px; text-align: center; background: white; border: 1px solid #dce2e4; border-top: 4px solid #173c52; border-radius: 3px; }
.state-card > .v-progress-circular { margin-bottom: 28px; }
.state-icon { display: flex; align-items: center; justify-content: center; width: 58px; height: 58px; margin: 0 auto 28px; border-radius: 50%; background: #faf4e7; color: #90733e; }
.state-icon svg { width: 30px; height: 30px; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; }
.verification-page .state-card h1 { max-width: 650px; margin-left: auto; margin-right: auto; font-size: 34px; }
.verification-page .state-card > p:last-of-type { max-width: 510px; margin: 16px auto 0; color: #5b707d; line-height: 1.8; font-size: 15px; }
.verification-page .retry-button { margin-top: 26px; text-transform: none; letter-spacing: 0; border-radius: 3px; }
.retry-button:focus-visible { outline: 3px solid #9c855c; outline-offset: 4px; }
@media (max-width: 760px) {
  .verification-page { padding: 0 20px; }
  .verification-header { padding: 22px 0; gap: 16px; }
  .ism-brand { width: 138px; }
  .header-label { max-width: 115px; font-size: 10px; line-height: 1.6; text-align: right; }
  .verification-content { padding: 32px 0 28px; }
  .verification-page h1 { font-size: 34px; }
  .page-introduction > p:last-child { font-size: 14px; }
  .record-heading { padding: 18px 22px; }
  .record-label { max-width: 160px; font-size: 11px; line-height: 1.8; }
  .record-label > span { margin: 0 5px; }
  .record-body { grid-template-columns: minmax(0, 1fr); }
  .record-details { padding: 28px 22px; }
  .verification-page .recipient-name { font-size: 29px; }
  .verification-page .qualification-name { font-size: 18px; }
  .record-metadata { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
  .verification-assurance { display: grid; grid-template-columns: 58px minmax(0, 1fr); gap: 10px 22px; text-align: left; padding: 26px 28px; border-left: 0; border-top: 1px solid #e8e4d9; }
  .verification-seal { width: 58px; height: 58px; padding: 11px; grid-row: span 2; outline-offset: 3px; }
  .verification-page .assurance-title { margin: 0; font-size: 18px; }
  .assurance-issuer { display: inline; }
  .assurance-rule { display: none; }
  .verification-page .assurance-copy { max-width: none; font-size: 11px; }
  .record-footer { align-items: flex-start; padding: 16px 22px; }
  .record-footer svg { margin-top: 2px; }
  .site-footer { flex-direction: column; gap: 5px; }
  .state-card { padding: 38px 24px; }
  .verification-page .state-card h1 { font-size: 29px; }
}
</style>
